import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMyContext } from './StateHolder';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../ducks/auth.duck';
import { SIGNUP } from '../util/enums';

// First create the context to hold shared functions.
// The default value is no-ops so consumers don't crash when rendered outside
// of a ContextFunctions provider (e.g. unit tests, isolated story setups).
const MyContextFunctions = React.createContext({
  onOpenAuthModal: () => {},
  onSetAuthType: () => {},
  onRequireAuth: () => {},
});

// Then create a ContextFunctions wrapper component
// to hold the shared functions that the components need.

const ContextFunctions = props => {
  // Bring stateholders from another context
  const { setOpenAuthModal, setAuthType, authContinuation, setAuthContinuation } = useMyContext();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const history = useHistory();

  const onOpenAuthModal = bool => {
    setOpenAuthModal(bool);
    if (!bool) {
      setAuthType('signup');
      // Closed without signing in: nothing to carry on to
      setAuthContinuation(null);
    }
  };

  // A guest needs an account for what they asked: the modal opens, and once they are signed
  // in they go on to `to` (a page) or `then` runs (open the message window)
  const onRequireAuth = ({ authType = SIGNUP, to = null, then = null } = {}) => {
    setAuthContinuation(to || then ? { to, then } : null);
    setAuthType(authType);
    setOpenAuthModal(true);
  };

  const onSetAuthType = type => {
    setAuthType(type);
  };

  useEffect(() => {
    if (isAuthenticated) {
      setOpenAuthModal(false);
      if (authContinuation) {
        const { to, then } = authContinuation;
        setAuthContinuation(null);
        if (to) history.push(to);
        if (typeof then === 'function') then();
      }
    }
  }, [isAuthenticated, setOpenAuthModal, authContinuation, setAuthContinuation, history]);

  return (
    <MyContextFunctions.Provider
      value={{
        onOpenAuthModal,
        onSetAuthType,
        onRequireAuth,
      }}
    >
      {props.children}
    </MyContextFunctions.Provider>
  );
};

export const useMyContextFunctions = () => useContext(MyContextFunctions);

export default ContextFunctions;
